import { action, extendObservable } from 'mobx';
import PropTypes from 'prop-types';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import * as d3 from 'd3';
import moment from 'moment';
import { apiCall, fileApiCall, repeatable } from '../API';
import debug from '../debug';
import { formatInteger, formatNumber } from '../format';
import AsyncActionStore from './AsyncActionStore';
import FlagStore from './FlagStore';
import DateRangeStore from './DateRangeStore';
import ReportDownloadStore from './ReportDownloadStore';

const makeApi = (url) => repeatable(apiCall(`/console/aggregates/${url}`, { isUserAction: false }));

// executes only the latest call
function makeCbWrapper() {
    let counter = 0;
    return (fn) => {
        counter += 1;
        const counterSnapshot = counter;
        return (...args) => {
            if (counterSnapshot === counter) {
                try {
                    fn(...args);
                } catch (e) {
                    // later errors could get swallowed in .catch of promise
                    // but most likely if something happens here — it is a legit error
                    debug.recordError(e);
                    throw e;
                }
            }
        };
    };
}

export class SimpleCounterStore {
    static PropType = PropTypes.shape({
        updateState: AsyncActionStore.PropType.isRequired,
        flip: FlagStore.PropType.isRequired,
        value: PropTypes.number.isRequired,
        formattedValueCompact: PropTypes.string.isRequired,
        formattedValueExact: PropTypes.string.isRequired,
    });

    constructor(data = {}, autoUpdate = false) {
        this.onUpdateWrapper = makeCbWrapper();

        extendObservable(this, /** @class SimpleCounterStore */{
            product: null,
            api: () => Promise.resolve({ value: 0 }),
            updateState: new AsyncActionStore(),
            flip: new FlagStore(false),
            value: 0,
            formattedValueCompact: '0',
            formattedValueExact: '0',
        }, data);

        if (autoUpdate) {
            this.update();
        }
    }

    onUpdate = action(({ value }) => {
        this.updateState.onSuccess();
        this.value = value || 0;
        this.formattedValueCompact = formatNumber(this.value);
        this.formattedValueExact = formatInteger(this.value);
    });

    update = action((product) => {
        this.product = product || this.product;
        this.updateState.setLoading();
        return this.api({ product: this.product })
            .then(this.onUpdateWrapper(this.onUpdate))
            .catch(debug.warn);
    });
}

export class ChartStore {
    static PropType = PropTypes.shape({
        updateState: AsyncActionStore.PropType.isRequired,
        setRangeId: PropTypes.func.isRequired,
        applyCustomRange: PropTypes.func.isRequired,
        cancelCustomRange: PropTypes.func.isRequired,
        dateRange: DateRangeStore.PropType.isRequired,
        rangePickerModal: FlagStore.PropType.isRequired,
    });

    constructor(data = {}, autoUpdate = false) {
        this.onUpdateWrapper = makeCbWrapper();

        this.points = [[], []];

        extendObservable(this, /** @class ChartStore */{
            product: null,
            ranges: [{
                id: 'WEEKLY',
                title: '7 Days',
                formatX: d3.utcFormat('%b %e'),
                getRange: () => [moment().subtract(7, 'days'), moment()],
            }, {
                id: 'MONTHLY',
                title: '30 Days',
                formatX: d3.utcFormat('%b %e'),
                getRange: () => [moment().subtract(30, 'days'), moment()],
            }, {
                id: 'YEARLY',
                title: '1 Year',
                formatX: d3.utcFormat('%b'),
                getRange: () => [moment().subtract(1, 'years'), moment()],
            }],
            rangeId: 'WEEKLY',
            dateRange: new DateRangeStore(),
            rangePickerModal: new FlagStore(false),
            get range() {
                return this.ranges.find((x) => x.id === this.rangeId);
            },
            api1: () => Promise.resolve({ total: 0, data: [] }),
            api2: () => Promise.resolve({ value: 0, data: [] }),
            updateState: new AsyncActionStore(),
            totals: [0, 0],
        }, data);

        this.dateRange.configure(...this.range.getRange());

        if (autoUpdate) {
            this.update();
        }
    }

    onUpdate = action((results) => {
        const formatPoint = ({ date, value }) => ({
            x: new Date(Math.floor(date / 1000)),
            y: value,
        });
        this.totals.replace(results.map((res) => res.total));
        this.points = results.map((res) =>
            res.data.map(formatPoint).sort((a, b) => a.x - b.x));
        this.updateState.onSuccess();
    });

    update = action((product) => {
        this.product = product || this.product;
        const args = {
            period: this.rangeId,
            product: this.product,
        };

        this.updateState.setLoading();
        return Promise.all([this.api1, this.api2]
            .map((api) => api(args)))
            .then(this.onUpdateWrapper(this.onUpdate))
            .catch(debug.warn);
    });

    setRangeId = action((id) => {
        if (this.range.id === id) {
            return;
        }
        this.rangeId = id;
        this.dateRange.configure(...this.range.getRange());
        this.update();
    });

    applyCustomRange = action(() => {
        this.dateRange.apply();
        this.rangePickerModal.close();
        this.update();
    });

    cancelCustomRange = action(() => {
        this.dateRange.reset();
        this.rangePickerModal.close();
    });
}


export class ListStore {
    static PropType = PropTypes.shape({
        updateState: AsyncActionStore.PropType.isRequired,
        items: MobxPropTypes.arrayOrObservableArrayOf(MobxPropTypes.objectOrObservableObject).isRequired,
        pageSize: PropTypes.number.isRequired,
        pageId: PropTypes.number.isRequired,
        pageCount: PropTypes.number.isRequired,
        showNextPage: PropTypes.func.isRequired,
        showPrevPage: PropTypes.func.isRequired,
    });

    constructor(data = {}, autoUpdate = false) {
        this.onUpdateWrapper = makeCbWrapper();

        extendObservable(this, /** @class ListStore */{
            product: null,
            updateState: new AsyncActionStore(),
            api: () => Promise.resolve([]),
            limit: 100,
            pageSize: 10,
            pageId: 0,
            pageCount: 0,
            items: [],
        }, data);

        if (autoUpdate) {
            this.update();
        }
    }

    onUpdate = action((data) => {
        this.updateState.onSuccess();
        this.items.replace(data.sort((a, b) => b.value - a.value));
        this.pageId = 0;
        this.pageCount = Math.ceil(this.items.length / this.pageSize);
    });

    update = action((product) => {
        this.product = product || this.product;
        this.updateState.setLoading();
        return this.api({ limit: this.limit, product: this.product })
            .then(this.onUpdateWrapper(this.onUpdate))
            .catch(debug.warn);
    });

    showNextPage = action(() => {
        this.pageId = (this.pageId + 1 + this.pageCount) % this.pageCount;
    });

    showPrevPage = action(() => {
        this.pageId = (this.pageId - 1 + this.pageCount) % this.pageCount;
    });
}


export class MultipleCountersStore {
    static PropType = PropTypes.shape({
        values: MobxPropTypes.arrayOrObservableArray,
        flip: FlagStore.PropType,
        tabId: PropTypes.number.isRequired,
        setTabId: PropTypes.func.isRequired,
        tabs: MobxPropTypes.arrayOrObservableArray,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class MultipleCountersStore */{
            values: [0, 0, 0, 0],
            flip: new FlagStore(false),
            tabs: [{
                title: '7 DAYS',
                label: 'Last 7 Days',
            }, {
                title: '30 DAYS',
                label: 'Last 30 Days',
            }, {
                title: '1 YEAR',
                label: 'Last Year',
            }],
            tabId: 1,
        }, data);
    }

    setValues = action((values) => {
        this.values.replace(values);
    });

    setTabId = action((id) => {
        this.tabId = id;
    });
}


export class CountersStore {
    static PropType = PropTypes.shape({
        updateState: AsyncActionStore.PropType.isRequired,
        downloads: MultipleCountersStore.PropType.isRequired,
        uploads: MultipleCountersStore.PropType.isRequired,
        scripts: MultipleCountersStore.PropType.isRequired,
    });

    constructor(data = {}, autoUpdate = false) {
        this.onUpdateWrapper = makeCbWrapper();

        extendObservable(this, /** @class CountersStore */{
            product: null,
            updateState: new AsyncActionStore(),
            api: () => Promise.resolve([]),
            downloads: new MultipleCountersStore(),
            uploads: new MultipleCountersStore(),
            scripts: new MultipleCountersStore(),
        }, data);

        if (autoUpdate) {
            this.update();
        }
    }

    onUpdate = action((data) => {
        this.updateState.onSuccess();
        this.downloads.setValues([
            data.downloads,
            data.downloads7days,
            data.downloads30days,
            data.downloads365days,
        ]);
        this.uploads.setValues([
            data.fileUploads,
            data.fileUploads7days,
            data.fileUploads30days,
            data.fileUploads365days,
        ]);
        this.scripts.setValues([
            data.externalJSDownloads,
            data.externalJSDownloads7days,
            data.externalJSDownloads30days,
            data.externalJSDownloads365days,
        ]);
    });

    update = action((product) => {
        this.product = product || this.product;
        this.updateState.setLoading();
        return this.api({ product: this.product })
            .then(this.onUpdateWrapper(this.onUpdate))
            .catch(debug.warn);
    });
}

export class DashboardStore {
    static PropType = PropTypes.shape({
        activeUsers: SimpleCounterStore.PropType.isRequired,
        activeEndpoints: SimpleCounterStore.PropType.isRequired,
        websitesDelivered: SimpleCounterStore.PropType.isRequired,
        pagesDelivered: SimpleCounterStore.PropType.isRequired,
        usersAndEndpoints: ChartStore.PropType.isRequired,
        websitesAndPages: ChartStore.PropType.isRequired,
        counters: CountersStore.PropType.isRequired,
        topSitesVisited: ListStore.PropType.isRequired,
        update: PropTypes.func.isRequired,
        stopUpdate: PropTypes.func.isRequired,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class DashboardStore */{
            activeUsers: new SimpleCounterStore({ api: makeApi('total-active-users') }),
            activeEndpoints: new SimpleCounterStore({ api: makeApi('total-active-endpoints') }),
            websitesDelivered: new SimpleCounterStore({ api: makeApi('total-websites-delivered') }),
            pagesDelivered: new SimpleCounterStore({ api: makeApi('total-page-delivered') }),
            usersAndEndpoints: new ChartStore({
                api1: makeApi('active-users-timely'),
                api2: makeApi('active-endpoints-timely'),
            }),
            websitesAndPages: new ChartStore({
                api1: makeApi('websites-delivered-timely'),
                api2: makeApi('pages-delivered-timely'),
            }),
            counters: new CountersStore({ api: makeApi('counters') }),
            topSitesVisited: new ListStore({ api: makeApi('top-websites-visited') }),
            topSitesBlocked: new ListStore({ api: makeApi('top-websites-blocked') }),
            reportDownload: new ReportDownloadStore({
                api: fileApiCall('/console/aggregates/create-ppt'),
                product: 'BROWSING_DEFENSE',
            }),
        }, data);

        this.updateTm = null;
    }

    load = action((onlyWebmail) => {
        return Promise.all([
            this.activeUsers,
            this.activeEndpoints,
            this.websitesDelivered,
            this.pagesDelivered,
            this.usersAndEndpoints,
            this.websitesAndPages,
            this.counters,
            this.topSitesVisited,
            this.topSitesBlocked,
        ].map((store) =>
            store.update(onlyWebmail ? 'WEBMAIL_DEFENSE' : 'BROWSING_DEFENSE')));
    });

    update = action((onlyWebmail) => {
        if (!this.updateTm) {
            this.load(onlyWebmail);
            this.updateTm = setTimeout(() => this.update(onlyWebmail), 10 * 60 * 1000);
        }
    });

    stopUpdate = action(() => clearTimeout(this.updateTm));
}

export class UrlIsoDashboardStore {
    static PropType = PropTypes.shape({
        totalRedirects: SimpleCounterStore.PropType.isRequired,
        totalThreatsBlocked: SimpleCounterStore.PropType.isRequired,
        redirects: ChartStore.PropType.isRequired,
        threatsBlocked: ChartStore.PropType.isRequired,
        counters: CountersStore.PropType.isRequired,
        topSitesVisited: ListStore.PropType.isRequired,
        update: PropTypes.func.isRequired,
        stopUpdate: PropTypes.func.isRequired,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class UrlIsoDashboardStore */{
            totalRedirects: new SimpleCounterStore({ api: makeApi('total-redirects') }),
            totalThreatsBlocked: new SimpleCounterStore({ api: makeApi('total-threats-blocked') }),
            redirects: new ChartStore({
                api1: makeApi('redirects-timely'),
            }),
            threatsBlocked: new ChartStore({
                api1: makeApi('threats-blocked-timely'),
            }),
            counters: new CountersStore({ api: makeApi('counters') }),
            topSitesVisited: new ListStore({ api: makeApi('top-websites-visited') }),
            topSitesBlocked: new ListStore({ api: makeApi('top-websites-blocked') }),
            reportDownload: new ReportDownloadStore({
                api: fileApiCall('/console/aggregates/create-ppt'),
                product: 'URL_ISOLATION',
            }),
        }, data);

        this.updateTm = null;
    }

    load = action(() => {
        return Promise.all([
            this.totalRedirects,
            this.totalThreatsBlocked,
            this.redirects,
            this.threatsBlocked,
            this.counters,
            this.topSitesVisited,
            this.topSitesBlocked,
        ].map((store) => store.update('URL_ISOLATION')));
    });

    update = action(() => {
        if (!this.updateTm) {
            this.load();
            this.updateTm = setTimeout(this.update, 10 * 60 * 1000);
        }
    });

    stopUpdate = action(() => clearTimeout(this.updateTm));
}
